import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatHour } from "@/lib/utils";
import { User, Check, X, DollarSign, Receipt, AlertTriangle, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import ReceiptModal from "@/components/ReceiptModal";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

function isPaidStatus(ps) {
  return ps && !ps.startsWith("pending");
}

function displayStatus(booking) {
  if (booking.status === "checked_in") return "checked_in";
  const paid = isPaidStatus(booking.payment_status);
  const endHour = booking.end_hour;
  const dateStr = booking.date;
  const now = new Date();
  const bookingEnd = new Date(dateStr + "T" + String(Math.floor(endHour)).padStart(2, "0") + ":" + String(Math.round((endHour % 1) * 60)).padStart(2, "0") + ":00");
  if (booking.status === "confirmed" && paid && bookingEnd < now) return "completed";
  return booking.status;
}

const STATUS_STYLES = {
  confirmed: "border-volt text-volt",
  pending: "border-yellow-400 text-yellow-400",
  cancelled: "border-muted-foreground/40 text-muted-foreground/80",
  rejected: "border-red-400 text-red-400",
  checked_in: "border-sky-400 text-sky-400",
  completed: "border-emerald-400 text-emerald-400",
};

export default function BookingDrawer({ booking: propBooking, onClose, onRefresh, t, formatPrice }) {
  const [override, setOverride] = useState(null);
  const booking = override || propBooking;
  const [showReceipt, setShowReceipt] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");

  useEffect(() => {
    setOverride(null);
    setCancelConfirm(false);
    setCancelReason("");
    setCancelReasonError("");
  }, [propBooking]);

  if (!propBooking) return null;

  const statusKey = displayStatus(booking);
  const payLabel = t(`bookings.payment.${booking.payment_status}`, booking.payment_status);

  const decide = async (id, decision) => {
    try {
      await api.patch(`/bookings/${id}/decision`, null, { params: { decision } });
      toast.success(decision === "confirmed" ? t("common.booking_approved") : t("common.booking_rejected"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.patch(`/bookings/${id}/mark-paid`);
      toast.success(t("common.booking_marked_paid"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetCancelDialog = () => {
    setCancelReason("");
    setCancelReasonError("");
  };

  const handleCancelConfirmChange = (open) => {
    setCancelConfirm(open);
    if (!open) resetCancelDialog();
  };

  const cancelBooking = async (id, cancellationReason) => {
    setCancelling(true);
    try {
      await api.patch(`/bookings/${id}/owner-cancel`, {
        cancellation_reason: cancellationReason,
      });
      toast.success(t("common.booking_cancelled"));
      setOverride({ ...booking, status: "cancelled", cancellation_reason: cancellationReason });
      handleCancelConfirmChange(false);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  const checkIn = async (id) => {
    try {
      const r = await api.post(`/bookings/${id}/checkin`);
      toast.success(t("owner.checked_in"));
      setOverride({ ...booking, ...r.data });
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkOut = async (id) => {
    try {
      const r = await api.post(`/bookings/${id}/checkout`);
      toast.success(t("owner.checked_out"));
      setOverride({ ...booking, ...r.data });
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const refundBooking = async (id) => {
    try {
      const r = await api.post(`/bookings/${id}/refund`);
      toast.success(t("owner.refund_success"));
      setOverride({ ...booking, ...r.data });
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      {booking && (
        <div className="fixed inset-0 z-40 bg-background/70" onClick={onClose} />
      )}
      <div className={`fixed top-0 end-0 z-50 h-full w-full max-w-md bg-background border-s border-border shadow-2xl transform transition-transform duration-300 ${booking ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <div className="label-eyebrow text-volt">{t("booking_details.eyebrow")}</div>
              <div className="font-heading text-xl tracking-tight mt-1">{booking.court_name}</div>
            </div>
            <button onClick={onClose} className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold">{t("common.close")}</button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            <div className="border border-border/30 bg-card p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-volt/10 text-volt flex items-center justify-center font-bold text-sm shrink-0">
                  {booking.user_name?.charAt(0) || '?'}
                </div>
                <div className="font-semibold text-sm min-w-0 truncate">{booking.user_name} {booking.is_manual && <span className="inline-block text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 bg-volt/15 text-volt rounded-sm">Manual</span>}</div>
              </div>
              <div className="flex gap-2 pl-[52px]">
                <Link to="/chats" className="flex-1 border border-border/50 rounded-sm px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors flex items-center justify-center gap-1">
                  <MessageCircle className="h-3.5 w-3.5" />{t("owner.message")}
                </Link>
                {booking.user_phone && <a href={`tel:${booking.user_phone}`} title={booking.user_phone} className="flex-1 border border-border/50 rounded-sm px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors flex items-center justify-center gap-1"><Phone className="h-3.5 w-3.5" />{t("owner.call")}</a>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1">
              <div className="border border-border/30 bg-card p-4">
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.date")}</div>
                <div className="font-semibold text-sm">{booking.date}</div>
              </div>
              <div className="border border-border/30 bg-card p-4">
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.time")}</div>
                <div className="font-semibold text-sm font-mono">{formatHour(booking.start_hour)} – {formatHour(booking.end_hour)}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1">
              <div className="border border-border/30 bg-card p-4">
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.status")}</div>
                <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border font-bold ${STATUS_STYLES[statusKey] || "border-border text-muted-foreground/80"}`}>
                  {t("bookings.status_" + statusKey, statusKey)}
                </span>
              </div>
              <div className="border border-border/30 bg-card p-4">
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.payment")}</div>
                <span className={`text-[10px] uppercase tracking-widest font-bold ${
                  booking.payment_status === "paid_at_court" ? "text-volt" :
                  booking.payment_status === "pending_cod" || booking.payment_status === "pending_online" ? "text-yellow-400" :
                  "text-emerald-400"
                }`}>
                  {payLabel}
                </span>
              </div>
            </div>

            <div className="border border-border/30 bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-widest uppercase text-muted-foreground/80">{t("booking_details.total")}</span>
                <span className="font-heading text-2xl text-volt">{formatPrice(booking.total_price, booking.currency)}</span>
              </div>
              {booking.selected_extras && booking.selected_extras.length > 0 && (
                <div className="border-t border-border pt-2 mt-2 space-y-1">
                  <div className="text-[9px] tracking-widest uppercase text-muted-foreground/60">{t("court.extras")}</div>
                  {booking.selected_extras.map((extra, i) => (
                    <div key={i} className="flex justify-between text-[11px]">
                      <span className="text-muted-foreground/80">{extra.name}{extra.qty > 1 ? ` x${extra.qty}` : ""}</span>
                      {extra.price > 0 && <span className="text-volt">+{formatPrice(extra.price * (extra.qty || 1), booking.currency)}</span>}
                    </div>
                  ))}
                </div>
              )}
              {booking.discount_label && booking.discount_amount > 0 && (
                <div className="border-t border-border pt-2 mt-2 flex justify-between text-[11px]">
                  <span className="text-emerald-400">{booking.discount_label}</span>
                  <span className="text-emerald-400">&minus;{formatPrice(booking.discount_amount, booking.currency)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 bg-background border-t border-border p-4 mt-auto space-y-2">
            <div className="space-y-2">
              {booking.status === "pending" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => decide(booking.id, "confirmed")}
                    className="flex-1 bg-volt text-primary-foreground py-2.5 text-[10px] tracking-widest uppercase font-bold flex items-center justify-center gap-2"
                  >
                    <Check className="h-3.5 w-3.5" /> {t("owner.approve")}
                  </button>
                  <button
                    onClick={() => decide(booking.id, "rejected")}
                    className="flex-1 border border-red-400 text-red-400 py-2.5 text-[10px] tracking-widest uppercase font-bold flex items-center justify-center gap-2 hover:bg-red-400 hover:text-primary-foreground"
                  >
                    <X className="h-3.5 w-3.5" /> {t("owner.reject")}
                  </button>
                </div>
              )}
              {booking.payment_status === "pending_cod" && booking.status !== "cancelled" && booking.status !== "rejected" && (
                <button
                  onClick={() => markPaid(booking.id)}
                  className="w-full border border-volt text-volt py-2.5 text-[10px] tracking-widest uppercase font-bold hover:bg-volt hover:text-primary-foreground transition-colors"
                >
                  <DollarSign className="h-3.5 w-3.5 inline me-2" /> {t("owner.mark_paid")}
                </button>
              )}
              {booking.status === "confirmed" && (
                <button
                  onClick={() => checkIn(booking.id)}
                  className="w-full border border-sky-400 text-sky-400 py-2.5 text-[10px] tracking-widest uppercase font-bold hover:bg-sky-400 hover:text-primary-foreground transition-colors"
                >
                  <User className="h-3.5 w-3.5 inline me-2" /> {t("owner.check_in")}
                </button>
              )}
              {booking.status === "checked_in" && (
                <button
                  onClick={() => checkOut(booking.id)}
                  className="w-full border border-emerald-400 text-emerald-400 py-2.5 text-[10px] tracking-widest uppercase font-bold hover:bg-emerald-400 hover:text-primary-foreground transition-colors"
                >
                  <Check className="h-3.5 w-3.5 inline me-2" /> {t("owner.check_out")}
                </button>
              )}
            </div>

            <button
              onClick={() => setShowReceipt(true)}
              className="w-full border border-border py-2.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:border-foreground/30 hover:text-foreground transition-colors flex items-center justify-center gap-2"
            >
              <Receipt className="h-3.5 w-3.5" /> {t("owner.view_receipt")}
            </button>
            {booking.status !== "cancelled" && booking.status !== "rejected" && (
               <button
                 onClick={() => setCancelConfirm(true)}
                 disabled={cancelling}
                 className="w-full border border-red-400/40 py-2.5 text-[10px] tracking-widest uppercase font-bold text-red-400/60 hover:border-red-400 hover:text-red-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
               >
                 <X className="h-3.5 w-3.5" /> {t("owner.cancel_booking")}
               </button>
             )}
             {(booking.payment_status === "paid_at_court" || booking.payment_status === "mocked_paid") && booking.status !== "cancelled" && booking.status !== "rejected" && (
               <button
                 onClick={() => refundBooking(booking.id)}
                 className="w-full border border-yellow-400/40 py-2.5 text-[10px] tracking-widest uppercase font-bold text-yellow-400/60 hover:border-yellow-400 hover:text-yellow-400 transition-colors flex items-center justify-center gap-2"
               >
                 <DollarSign className="h-3.5 w-3.5" /> {t("owner.refund")}
               </button>
             )}
          </div>

          <div className="border-t border-border px-5 py-4">
            <Link
              to={`/bookings/${booking.id}`}
              onClick={onClose}
              className="btn-outline rounded-sm w-full py-2.5 text-[10px] tracking-widest uppercase font-bold flex items-center justify-center gap-2"
            >
              {t("owner.view_full_details")}
            </Link>
          </div>
        </div>
      </div>
      <AlertDialog open={cancelConfirm} onOpenChange={handleCancelConfirmChange}>
        <AlertDialogContent className="bg-background border border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {t("owner.cancel_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground/80 text-sm">
              {t("owner.cancel_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="owner-booking-cancel-reason" className="text-xs font-medium text-foreground">
              {t("owner.cancel_reason_label")}
            </label>
            <Textarea
              id="owner-booking-cancel-reason"
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                if (cancelReasonError && e.target.value.trim()) {
                  setCancelReasonError("");
                }
              }}
              placeholder={t("owner.cancel_reason_placeholder")}
              disabled={cancelling}
              aria-invalid={cancelReasonError ? "true" : "false"}
              className="min-h-[96px] border-border bg-card"
            />
            {cancelReasonError && (
              <p className="text-xs text-red-400">{cancelReasonError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-border text-muted-foreground/80 hover:bg-card hover:text-foreground">
              {t("common.close")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                const reason = cancelReason.trim();
                if (!reason) {
                  e.preventDefault();
                  setCancelReasonError(t("owner.cancel_reason_required"));
                  return;
                }
                cancelBooking(booking.id, reason);
              }}
              disabled={cancelling}
              className="bg-red-500 text-foreground hover:bg-red-600"
            >
              {cancelling ? t("common.loading") : t("owner.cancel_booking")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReceiptModal
        open={showReceipt}
        onOpenChange={setShowReceipt}
        booking={booking}
        t={t}
      />
    </>
  );
}
